import { Injectable } from 'angular-ts-decorators';

import { Initable } from '@shared/app/scripts/services/initable';

import { SwDexie } from './sw-dexie';
import { ILocalDraftCollections } from '@core/app/scripts/services/local-draft-collections';
import { concat, keys, mapValues } from 'lodash';

/**
 * The localDraft service can store draft objects in IndexedDb.
 *
 * Implements {@link sharedModule.Initable Initable} interface.
 *
 * # Usage
 *
 * To use this service, first add collection(s) with {@link
 * coreModule.localDraftProvider#addCollection
 * localDraftProvider#addCollection}, then run {@link
 * coreModule.localDraft#init localDraft#init} on  the app start up.
 */
@Injectable('localDraft')
export class LocalDraft implements Initable {
  private VERSION = 2;
  private DATABASE_NAME = 'swLocalDrafts-v3';

  constructor(
    private $q: ng.IQService,
    private $log: ng.ILogService,
    private swDexie: SwDexie,
    private localDraftCollections: ILocalDraftCollections,
  ) {
    'ngInject';
  }

  /**
   * This method initializes localDraft.
   * @returns {object} $q promise that resolves empty if everything goes fine,
   * and rejects otherwise
   */
  init(): ng.IPromise<any> {
    if (keys(this.localDraftCollections).length === 0) {
      throw new Error('localDraft: no collection configured');
    }

    // here we override value that is shared across all swDexie uses
    // TODO refactor - it should be either controlled by the swDexie service,
    // or allow for flexibility. As it it now, the localDraft service is
    // kindnapping the swDexie db as soon as it's started
    this.swDexie.databaseName = this.DATABASE_NAME;

    const allColections = mapValues(this.localDraftCollections, (value) => {
      if (!value.additionalIndexKeys) {
        return value.primaryKey;
      }

      return concat([value.primaryKey], value.additionalIndexKeys).join(',');
    });

    return this.swDexie.init(allColections, this.VERSION);
  }

  /**
   *
   * This method can be used to create a new draft
   * @param {string} collection the collection name of the draft.
   * @param {object} draft the unique id of the draft.
   *
   * @returns {object} $q promise that resolves to array with data
   */
  new(collection: string, draft: object): ng.IPromise<void | string> {
    // @ts-ignore find better type for those functions
    return this.swDexie.put(collection, draft).catch((error) => {
      this.$log.error(
        `[localDraft]: collection ${collection}: new error:`,
        error,
      );
    });
  }

  /**
   *
   * This method can be used to get a localDraft by its unique id
   * @param {string} collection the collection name of the draft.
   * @param {string} id the unique id of the draft.
   *
   * @returns {object} $q promise that resolves to array with data
   */
  get(collection: string, id: string): ng.IPromise<object> {
    return this.swDexie
      .getByKey(
        collection,
        this.localDraftCollections[collection].primaryKey,
        id,
      )
      .then((orders) => {
        if (angular.isArray(orders) && orders.length) {
          return orders[0];
        } else {
          return this.$q.reject('order not found');
        }
      });
  }

  /**
   * This method can be used to get all emelements in a given collection
   * @param {string} collection the collection name of the draft.
   * @returns {Promise} $q promise that resolves to array with data
   */
  getAll(collection: string): ng.IPromise<object[]> {
    return this.swDexie.getCollection(collection).then((orders) => {
      if (angular.isArray(orders)) {
        return orders;
      } else {
        return this.$q.reject('order not found');
      }
    });
  }

  /**
   *
   * This method can be used to find drafts by a particular key
   * @param {string} collection the collection name of the draft.
   * @param {string} key the key to search by.
   * @param {string} value the  value to search for.
   *
   * @returns {object} $q promise that resolves to array with data
   */
  findByKey(
    collection: string,
    key: string,
    value: string,
  ): ng.IPromise<void | object[]> {
    return this.swDexie
      .findMany(collection, key, value)
      .then((orders) => {
        if (angular.isArray(orders)) {
          return orders;
        } else {
          return this.$q.reject('no drafts found');
        }
      })
      .catch((error) => {
        this.$log.error(
          `[localDraft]: collection ${collection}: findByKey error:`,
          error,
        );
      });
  }

  /**
   *
   * This method can be used to modify a localDraft by its unique id. If the
   * draft does not exist, it will be created.
   * @param {string} collection the collection name of the draft.
   * @param {object} draft the draft object.
   *
   * @returns {promise} $q promise that resolves to object
   */
  modify(collection: string, draft: object): ng.IPromise<void> {
    return this.swDexie.put(collection, draft).then(
      () => {
        this.$log.info(
          `[localDraft]: collection ${collection}: ${
            draft[this.localDraftCollections[collection].primaryKey]
          } modified ...`,
        );
      },
      (error) => {
        this.$log.error(
          `[localDraft]: collection ${collection}: ${
            draft[this.localDraftCollections[collection].primaryKey]
          } modify error:`,
          error,
        );
      },
    );
  }

  /**
   *
   * This method can be used to delete a draft by its unique id
   * @param {string} collection the collection name of the draft.
   * @param {string} id the unique id of the draft.
   *
   * @returns {object} $q promise that resolves to array with data
   */
  remove(collection: string, id: string): ng.IPromise<void> {
    return this.swDexie
      .remove(collection, this.localDraftCollections[collection].primaryKey, id)
      .then(() => {
        this.$log.info(
          `[localDraft]: collection ${collection}: ${id} removed ...`,
        );
      })
      .catch((error) => {
        this.$log.error(
          `[localDraft]: collection ${collection}: ${id} not removed, error:`,
          error,
        );
      });
  }

  /**
   *
   * This method can be used to delete a draft by its unique id
   * @param {string} collection the collection name of the draft.
   *
   * @returns {object} $q promise that resolves to array with data
   */
  dropCollection(collection: string): ng.IPromise<void> {
    return this.swDexie
      .dropCollection(collection)
      .then(() => {
        this.$log.info(`[localDraft]: collection ${collection} dropped`);
      })
      .catch((error) => {
        this.$log.error(
          `[localDraft]: collection ${collection}: drop error:`,
          error,
        );
      });
  }
}
